/**
 * GTM dataLayer utility for custom event tracking.
 * All events are pushed to window.dataLayer for Google Tag Manager to pick up.
 */

declare global {
  // eslint-disable-next-line no-unused-vars
  interface Window {
    dataLayer: Record<string, unknown>[];
    // Meta (Facebook/Instagram) Pixel — created by MetaPixel.tsx. Optional so
    // calls are safely no-ops when the pixel is disabled or not yet loaded.
    // eslint-disable-next-line no-unused-vars
    fbq?: (..._args: unknown[]) => void;
    // Google tag (gtag.js) for the Google Ads account — created by
    // GoogleAdsConversion.tsx. Optional so calls are safe no-ops when disabled.
    // eslint-disable-next-line no-unused-vars
    gtag?: (..._args: unknown[]) => void;
  }
}

/**
 * Push a custom event to the GTM dataLayer.
 */
export function pushEvent(
  eventName: string,
  params?: Record<string, unknown>
): void {
  try {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: eventName,
      page_path: window.location?.pathname,
      ...params,
    });
  } catch (error) {
    console.error('[dataLayer] Error pushing event:', error);
  }
}

export function normalizeTrackingName(value: string): string {
  return value
    .trim()
    .toLowerCase()
    .replace(/&/g, ' and ')
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');
}

/**
 * Track a form submission attempt/success event.
 */
export function trackFormSubmission(
  formName: string,
  data?: Record<string, unknown>
): void {
  pushEvent('form_submission', {
    form_name: formName,
    ...data,
  });
}

/**
 * Track a successful contact-form save. Fire this only after the Supabase insert
 * succeeds so GTM/GA4/Clarity do not count failed validation or database errors
 * as real leads.
 */
export function trackContactFormSuccess(
  formName: string,
  data?: Record<string, unknown>
): void {
  pushEvent('contact_form_success', {
    form_name: formName,
    ...data,
  });
}

/**
 * Track a CTA button click.
 */
export function trackCtaClick(
  ctaName: string,
  location: string,
  params?: Record<string, unknown>
): void {
  pushEvent('cta_click', {
    cta_name: ctaName,
    cta_location: location,
    ...params,
  });
}

/**
 * Track a phone link click.
 */
export function trackPhoneClick(
  phoneNumber: string,
  params?: Record<string, unknown>
): void {
  pushEvent('phone_click', {
    phone_number: phoneNumber,
    ...params,
  });
}

/**
 * Track a business email link click. This only sends Elev8-owned addresses,
 * not visitor-entered email values.
 */
export function trackEmailClick(
  emailAddress: string,
  params?: Record<string, unknown>
): void {
  const emailDomain = emailAddress.split('@')[1] || '';
  pushEvent('email_click', {
    email_address: emailAddress,
    email_domain: emailDomain,
    ...params,
  });
}

/**
 * Track the first user interaction with a form. No visitor-entered PII is sent.
 */
export function trackFormStart(
  formName: string,
  params?: Record<string, unknown>
): void {
  pushEvent('form_start', {
    form_name: formName,
    ...params,
  });
}

/**
 * Track a newsletter signup (does NOT include the actual email for privacy).
 */
export function trackNewsletterSignup(): void {
  pushEvent('newsletter_signup', {
    signup_location: 'footer',
  });
}

/**
 * Track a qualified lead using GA4's recommended `generate_lead` event. This
 * is the single canonical conversion across every lead path (contact form,
 * callback request, landing-page trials), so GTM needs only one tag and GA4
 * only one Key Event. No PII is sent — only the originating surface.
 */
export function trackGenerateLead(
  source: string,
  params?: Record<string, unknown>
): void {
  pushEvent('generate_lead', {
    lead_source: source,
    ...params,
  });

  // Mirror the conversion to Meta's standard `Lead` event so Facebook/Instagram
  // ads can optimize and measure. No visitor-entered PII is sent — only the
  // originating surface. Fires from this single chokepoint to avoid double-counting.
  try {
    window.fbq?.('track', 'Lead', { content_category: source });
  } catch (error) {
    console.error('[MetaPixel] Error tracking Lead:', error);
  }

  // Mirror the conversion to Google Ads ("Website Lead - Form Submit") so Smart
  // Bidding can optimize and measure. Same single chokepoint as Meta — covers
  // every lead path (contact form, both landing pages, AI calls) with no
  // double-count. The ID/label are public (they ship to the browser), so safe
  // to hardcode; the gtag base is loaded by GoogleAdsConversion.tsx.
  try {
    window.gtag?.('event', 'conversion', {
      send_to: 'AW-17422177310/BEWpCMCbkMMcEJ6oxfNA',
    });
  } catch (error) {
    console.error('[GoogleAds] Error tracking conversion:', error);
  }
}

/* -------------------------------------------------------------------------- */
/* Ecommerce (review stands shop)                                             */
/* -------------------------------------------------------------------------- */

/**
 * A catalog product as the page knows it. The field names match
 * `ReviewStandProduct` in `src/data/reviewStands.ts` on purpose: a call site
 * hands the catalog entry straight over instead of re-typing a price, which is
 * the only way a page can advertise one number and report another.
 */
export interface EcommerceProduct {
  sku: string;
  name: string;
  displayPriceCents: number;
}

/**
 * A line on a completed order, exactly as `GET /api/shop/order-status` returns
 * it (snake_case included), so the thank-you page can pass the response through
 * without reshaping money.
 */
export interface PurchasedItem {
  sku: string;
  name: string;
  quantity: number;
  unit_amount_cents: number;
}

export interface PurchaseOrder {
  /** Stripe Checkout Session id. Doubles as the Meta deduplication key. */
  sessionId: string;
  orderNumber: string;
  /** What Stripe actually charged, tax and shipping included. */
  totalCents: number;
  /** Stripe reports lowercase ('usd'); GA4 and Meta want ISO uppercase. */
  currency: string;
  items: PurchasedItem[];
}

/** Cents to currency units. GA4 and Meta both read `value` in dollars. */
function toAmount(cents: number): number {
  return Math.round(cents) / 100;
}

function ga4Item(sku: string, name: string, cents: number, quantity: number) {
  return {
    item_id: sku,
    item_name: name,
    price: toAmount(cents),
    quantity,
  };
}

/**
 * Meta pixel calls are best-effort decoration on top of the dataLayer push.
 * The pixel may be disabled, blocked, or simply not loaded yet, so every call
 * is optional-chained and wrapped: a missing pixel must never cost a sale.
 */
function trackMeta(
  eventName: string,
  params: Record<string, unknown>,
  options?: Record<string, unknown>
): void {
  try {
    if (options) {
      window.fbq?.('track', eventName, params, options);
    } else {
      window.fbq?.('track', eventName, params);
    }
  } catch (error) {
    console.error(`[MetaPixel] Error tracking ${eventName}:`, error);
  }
}

/**
 * Product page view. GA4 `view_item` plus Meta `ViewContent`.
 */
export function trackViewItem(product: EcommerceProduct): void {
  const value = toAmount(product.displayPriceCents);

  pushEvent('view_item', {
    ecommerce: {
      currency: 'USD',
      value,
      items: [ga4Item(product.sku, product.name, product.displayPriceCents, 1)],
    },
  });

  trackMeta('ViewContent', {
    content_ids: [product.sku],
    content_type: 'product',
    value,
    currency: 'USD',
  });
}

/**
 * "Buy now" clicked, before the redirect to Stripe. GA4 `begin_checkout` plus
 * Meta `InitiateCheckout`. The value is the line total, not the unit price.
 */
export function trackBeginCheckout(
  product: EcommerceProduct,
  quantity: number
): void {
  const value = toAmount(product.displayPriceCents * quantity);

  pushEvent('begin_checkout', {
    ecommerce: {
      currency: 'USD',
      value,
      items: [
        ga4Item(product.sku, product.name, product.displayPriceCents, quantity),
      ],
    },
  });

  trackMeta('InitiateCheckout', {
    content_ids: [product.sku],
    content_type: 'product',
    value,
    currency: 'USD',
    num_items: quantity,
  });
}

/**
 * A paid order, fired from the thank-you page. GA4 `purchase` plus Meta
 * `Purchase`.
 *
 * `eventID` is the Stripe Checkout Session id, which is also the `event_id` the
 * server sends to the Conversions API. Meta uses the pair to collapse the
 * browser event and the server event into one conversion; without it the same
 * sale is counted twice and Meta optimizes against inflated numbers.
 *
 * Money comes from the order, never from the catalog, so tax, shipping and any
 * promotion code are reflected in what GA4 records as revenue.
 */
export function trackPurchase(order: PurchaseOrder): void {
  const value = toAmount(order.totalCents);
  const currency = (order.currency || 'usd').toUpperCase();
  const numItems = order.items.reduce(
    (total, item) => total + item.quantity,
    0
  );

  pushEvent('purchase', {
    ecommerce: {
      transaction_id: order.orderNumber,
      value,
      currency,
      items: order.items.map(item =>
        ga4Item(item.sku, item.name, item.unit_amount_cents, item.quantity)
      ),
    },
  });

  trackMeta(
    'Purchase',
    {
      value,
      currency,
      content_ids: order.items.map(item => item.sku),
      content_type: 'product',
      num_items: numItems,
    },
    { eventID: order.sessionId }
  );
}
